import {
  AccountGroup,
  AccountInfo,
  Box,
  BulletPointsList,
  Button,
  Card,
  DefineTermDescription,
  Divider,
  Flex,
  Image,
  LabeledText,
  Space,
  Tag,
  Text,
} from '@/components';
import { UploadedAttachment } from '@/features/attachment/_components/UploadedAttachment';
import { TagTarget } from '@/features/tag/components';
import { useRecursiveOrganizationFilteredByKey } from '@/features/tenant-manager/organization/_hooks/organization';
import { useParseUsersByIds } from '@/features/tenant-manager/user/_hooks/user';
import { ITEM_SPLITTER, NEST_ITEM_CONNECTOR, datetimeFormat } from '@/utils';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { type FC, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

import type { CommentModel } from '@/features/comment/models/comment';
import { CommentUnreadBadge } from '../../common/CommentUnreadBadge';

const labelProps = {
  labelVariant: 'footnote',
  labelWeight: 'regular',
  labelColor: 'secondary',
} as const;

type Props = Pick<
  z.infer<typeof CommentModel>,
  | 'sharedOrganizationIds'
  | 'authorId'
  | 'assigneeIds'
  | 'relatedUserIds'
  | 'createdAt'
  | 'tagTargets'
  | 'label'
  | 'attachments'
  | 'content'
> &
  (
    | ({
        type: 'comment';
      } & Pick<z.infer<typeof CommentModel>, 'publishType' | 'readStatus'>)
    | {
        type: 'comment-archive';
      }
  );

export const CommentDetailsCard: FC<Props> = ({
  sharedOrganizationIds,
  authorId,
  assigneeIds,
  relatedUserIds,
  createdAt,
  tagTargets,
  label,
  attachments,
  content,
  ...rest
}) => {
  const { t } = useTranslation();

  const organizationNames = useRecursiveOrganizationFilteredByKey({
    enabled: !!sharedOrganizationIds.length,
    params: {
      ids: sharedOrganizationIds,
    },
    options: {
      filterBy: 'name',
      separator: NEST_ITEM_CONNECTOR,
      reverse: true,
    },
  });

  const [isCardExpanded, toggleIsCardExpanded] = useReducer(
    (state) => !state,
    false,
  );

  const isPrivateComment =
    rest.type === 'comment' && rest.publishType === 'private';

  const userIdsForParse = [authorId, ...assigneeIds, ...relatedUserIds];
  const { parseUserById, parseUsersByIds } =
    useParseUsersByIds(userIdsForParse);
  const author = parseUserById(authorId);
  const assignees = parseUsersByIds(assigneeIds);
  const relatedUsers = parseUsersByIds(relatedUserIds);

  return (
    <Card size="small" style={{ position: 'relative' }}>
      <Flex vertical gap={8}>
        <Space
          size="small"
          align="center"
          style={{ justifyContent: 'space-between' }}
        >
          <LabeledText
            direction="horizontal"
            label={t('created-date')}
            {...labelProps}
            text={datetimeFormat({
              datetime: createdAt,
              format: 'YYYY/MM/DD HH:mm',
            })}
            weight="regular"
          />
          {rest.type === 'comment' && rest.readStatus === 'unread' && (
            <CommentUnreadBadge />
          )}
        </Space>
        <Space align="center" size="small">
          <Box flex="none">
            <Text
              text={t('author')}
              weight={labelProps.labelWeight}
              variant={labelProps.labelVariant}
              color={labelProps.labelColor}
              maxRows={1}
            />
          </Box>
          <Box flex="auto">
            {author && (
              <AccountInfo
                iconUrl={author.iconDataUrl}
                accountName={author.name}
                organizationName={author.organizations
                  ?.map((d) => d.name)
                  .join(ITEM_SPLITTER)}
                maxWidth={180}
              />
            )}
          </Box>
        </Space>
        {!isPrivateComment && (
          <Space align="center" size="small">
            <Text
              text={t('comment.pinpoint-assignee')}
              weight="regular"
              variant="footnote"
              color="secondary"
              maxRows={1}
            />
            <AccountGroup
              accountInfoList={assignees.map((assignee) => {
                return {
                  iconUrl: assignee.iconDataUrl,
                  accountName: assignee.name,
                  organizationName: assignee.organizations
                    ?.map((d) => d.name)
                    .join(ITEM_SPLITTER),
                };
              })}
            />
          </Space>
        )}

        {isCardExpanded && (
          <>
            {!isPrivateComment && (
              <>
                <LabeledText
                  direction="horizontal"
                  label={t('viewable-organization')}
                  {...labelProps}
                  wrapperStyle={{
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                  }}
                  text={
                    <Box>
                      {organizationNames.data &&
                        organizationNames.data?.length > 0 && (
                          <Flex vertical>
                            <BulletPointsList
                              list={organizationNames.data.map((name) => (
                                <Text key={name} text={name} weight="regular" />
                              ))}
                            />
                          </Flex>
                        )}
                    </Box>
                  }
                />
                <LabeledText
                  direction="horizontal"
                  label={t('viewable-user')}
                  {...labelProps}
                  text={
                    <Box>
                      <AccountGroup
                        accountInfoList={relatedUsers.map((relatedUser) => {
                          return {
                            iconUrl: relatedUser.iconDataUrl,
                            accountName: relatedUser.name,
                            organizationName: relatedUser.organizations
                              ?.map((d) => d.name)
                              .join(ITEM_SPLITTER),
                          };
                        })}
                      />
                    </Box>
                  }
                />
              </>
            )}
            <LabeledText
              direction="horizontal"
              label={t('comment.label')}
              {...labelProps}
              text={
                label?.name ? (
                  <Tag bordered style={{ borderWidth: 2 }}>
                    {label.name}
                  </Tag>
                ) : null
              }
            />
            <Image.PreviewGroup>
              <DefineTermDescription
                term={t('attachment-file')}
                items={
                  attachments
                    ? attachments.map((attachment) => (
                        <UploadedAttachment
                          key={attachment.id}
                          attachment={attachment}
                        />
                      ))
                    : []
                }
              />
            </Image.PreviewGroup>
          </>
        )}
        <Space align="center" size="small" style={{ justifyContent: 'end' }}>
          <Button
            type="link"
            onClick={toggleIsCardExpanded}
            size="small"
            style={{ padding: 0, border: 0, fontSize: 'small' }}
          >
            {isCardExpanded ? (
              <Space>
                <span>{t('close-items')}</span>
                <UpOutlined style={{ fontSize: 'small' }} />
              </Space>
            ) : (
              <Space>
                <span>{t('open-items')}</span>
                <DownOutlined style={{ fontSize: 'small' }} />
              </Space>
            )}
          </Button>
        </Space>

        <Divider />

        <Flex vertical gap={4}>
          {!!tagTargets.length && (
            <Space wrap size={[0, 8]}>
              {tagTargets.map((tagTarget) => (
                <TagTarget key={tagTarget.tagId} tag={tagTarget.tag} />
              ))}
            </Space>
          )}
          <Text text={content} weight="regular" />
        </Flex>
      </Flex>
    </Card>
  );
};
